import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useLayoutEffect, useState } from 'react';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { useQuill } from '../QuillContext';
import FloatingToolbar from '../controls/toolbar/FloatingToolbar';
import ToolbarButton, {
  ToolbarButtonGroup,
} from '../controls/toolbar/buttons/ToolbarButton';
import ToolbarSpanButtonGroup from '../controls/toolbar/buttons/ToolbarSpanButtonGroup';
import formatBlotWithoutScrolling from '../controls/toolbar/formatBlotWithoutScrolling';
import * as styles from './TableToolbar.module.scss';
import type { Selection } from './useTableSelection';
import { SelectionType, getRowColumnItems } from './useTableSelection';

interface TableToolbarProps {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  blot: any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  span: any;
  tableContainer: HTMLElement;
  selection: Selection | null;
  onDelete: () => void;
}

const TableToolbar = ({
  blot,
  span,
  tableContainer,
  selection,
  onDelete,
}: TableToolbarProps) => {
  const { quill } = useQuill();
  const [rect, setRect] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!quill) return undefined;

    const [items, isRow] = getRowColumnItems(selection);
    const getWidth = () => tableContainer.getBoundingClientRect().width;
    let lastWidth = getWidth();

    const recalculate = () => {
      const firstItem = _.min(items);
      const element =
        firstItem != null &&
        tableContainer.querySelector(
          `[data-table-${isRow ? 'row' : 'column'}="${firstItem}"]`,
        );

      const containerRect = quill.container.getBoundingClientRect();
      const tableContainerRect = tableContainer.getBoundingClientRect();
      if (element) {
        const elementRect = element.getBoundingClientRect();
        const left = elementRect.left - containerRect.left;
        const top = elementRect.top - containerRect.top;
        setRect(
          isRow ? { top, left } : { top, left: left + elementRect.width },
        );
      } else if (selection?.type === SelectionType.Table) {
        setRect({
          top:
            tableContainerRect.top -
            parseInt(styles.insertButtonSize, 10) -
            containerRect.top,
          left: tableContainerRect.left - containerRect.left,
        });
      } else {
        setRect(null);
      }
    };

    recalculate();

    const observer = new ResizeObserver(() => {
      const width = getWidth();
      if (width === lastWidth) return;
      lastWidth = width;
      recalculate();
    });

    observer.observe(tableContainer);

    return () => {
      observer.disconnect();
    };
  }, [selection, tableContainer, quill]);

  const format = (attributes: Record<string, unknown>) => {
    if (!quill) return;
    formatBlotWithoutScrolling({ quill, blot, attributes });
  };

  if (!rect) return null;
  if (
    selection?.type !== SelectionType.Table &&
    selection?.type !== SelectionType.Column &&
    selection?.type !== SelectionType.Row
  ) {
    return null;
  }

  return (
    <FloatingToolbar.Portal>
      <div
        data-no-print
        className={classNames(styles.container, {
          [styles.table]: selection?.type === SelectionType.Table,
          [styles.column]: selection?.type === SelectionType.Column,
          [styles.row]: selection?.type === SelectionType.Row,
        })}
        role="toolbar"
        style={rect}
      >
        {selection?.type === SelectionType.Table && (
          <ToolbarSpanButtonGroup
            span={span}
            onSpanChange={(newSpan) => format({ span: newSpan })}
          />
        )}
        <ToolbarButtonGroup>
          <ToolbarButton
            onClick={onDelete}
            icon={trashIcon}
            aria-label="Delete table"
            tooltip="Delete"
          />
        </ToolbarButtonGroup>
      </div>
    </FloatingToolbar.Portal>
  );
};

export default TableToolbar;
